<template>
  <div class="login-page">
    <div class="login-card brand-card">
      <div class="login-header">
        <img v-if="loginLogoUrl" class="login-logo" :src="loginLogoUrl" alt="Logo" />
        <p>配送系统管理后台</p>
      </div>

      <el-form v-if="step === 'password'" ref="passwordFormRef" :model="passwordForm" :rules="passwordRules" label-position="top">
        <el-form-item label="账号" prop="username">
          <el-input v-model="passwordForm.username" placeholder="请输入账号" @keydown.enter.native.prevent="submitPassword" />
        </el-form-item>
        <el-form-item label="密码" prop="password">
          <el-input v-model="passwordForm.password" show-password placeholder="请输入密码" @keydown.enter.native.prevent="submitPassword" />
        </el-form-item>
        <el-form-item label="图片验证码" prop="captcha_code">
          <div class="captcha-row">
            <el-input v-model="passwordForm.captcha_code" placeholder="请输入验证码" @keydown.enter.native.prevent="submitPassword" />
            <div class="captcha-image-wrap" @click="loadCaptcha">
              <img v-if="captchaImage" class="captcha-image" :src="captchaImage" alt="图片验证码" />
              <span v-else class="captcha-loading">点击刷新</span>
            </div>
          </div>
        </el-form-item>
        <el-button native-type="button" type="primary" :loading="loading" class="submit-btn" @click="submitPassword">登录</el-button>
        <div class="login-links">
          <el-button native-type="button" type="text" @click="openResetDialog">忘记密码</el-button>
        </div>
      </el-form>

      <el-form v-else ref="smsFormRef" :model="smsForm" :rules="smsRules" label-position="top">
        <el-alert
          title="账号密码验证通过，请输入短信验证码完成登录"
          type="info"
          :closable="false"
          style="margin-bottom: 16px;"
        />
        <el-form-item label="短信验证码" prop="sms_code">
          <el-input v-model="smsForm.sms_code" placeholder="请输入短信验证码" @keydown.enter.native.prevent="submitSms" />
        </el-form-item>
        <div class="sms-actions">
          <el-button native-type="button" type="primary" :loading="loading" class="submit-btn" @click="submitSms">验证并登录</el-button>
          <el-button native-type="button" class="submit-btn" @click="backToPassword">返回上一页</el-button>
        </div>
      </el-form>
    </div>

    <el-dialog title="找回密码" :visible.sync="showResetDialog" width="520px" :close-on-click-modal="false" @close="resetResetForm">
      <el-form :model="resetForm" label-position="top">
        <el-form-item label="手机号">
          <el-input v-model="resetForm.phone" />
        </el-form-item>
        <el-form-item label="短信验证码">
          <div class="captcha-row">
            <el-input v-model="resetForm.sms_code" placeholder="请输入短信验证码" />
            <el-button :loading="resetSmsSending" :disabled="!resetForm.phone || !resetCaptchaCode" @click="sendResetSmsCode">发送验证码</el-button>
          </div>
        </el-form-item>
        <el-form-item label="姓名">
          <el-input v-model="resetForm.name" />
        </el-form-item>
        <el-form-item label="身份证号码">
          <el-input v-model="resetForm.id_card_no" />
        </el-form-item>
        <el-form-item label="新密码">
          <el-input v-model="resetForm.new_password" show-password />
        </el-form-item>
        <el-form-item label="图片验证码">
          <div class="captcha-row">
            <el-input v-model="resetCaptchaCode" placeholder="请输入图片验证码" />
            <div class="captcha-image-wrap" @click="loadResetCaptcha">
              <img v-if="resetCaptchaImage" class="captcha-image" :src="resetCaptchaImage" alt="图片验证码" />
              <span v-else class="captcha-loading">点击刷新</span>
            </div>
          </div>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="showResetDialog = false">取消</el-button>
        <el-button type="primary" :loading="resetting" @click="submitResetPassword">重置密码</el-button>
      </span>
    </el-dialog>

    <IcpFooter />
  </div>
</template>

<script>
import {
  fetchCaptcha,
  fetchPublicSystemSettings,
  login,
  loginAdminVerify,
  resetPasswordBySms,
  sendAuthSmsCode,
} from "../api/orders";
import IcpFooter from "../components/IcpFooter.vue";

export default {
  components: {
    IcpFooter,
  },
  data() {
    return {
      loading: false,
      resetting: false,
      resetSmsSending: false,
      step: "password",
      captchaImage: "",
      captchaId: "",
      loginLogoUrl: "",
      verifyToken: "",
      showResetDialog: false,
      passwordForm: { username: "", password: "", captcha_code: "" },
      smsForm: { sms_code: "" },
      resetCaptchaCode: "",
      resetCaptchaId: "",
      resetCaptchaImage: "",
      resetForm: { phone: "", sms_code: "", name: "", id_card_no: "", new_password: "" },
      passwordRules: {
        username: [{ required: true, message: "请输入账号", trigger: "blur" }],
        password: [{ required: true, message: "请输入密码", trigger: "blur" }],
        captcha_code: [{ required: true, message: "请输入图片验证码", trigger: "blur" }],
      },
      smsRules: {
        sms_code: [{ required: true, message: "请输入短信验证码", trigger: "blur" }],
      },
    };
  },
  mounted() {
    this.loadPublicSettings();
    this.loadCaptcha();
  },
  methods: {
    async loadPublicSettings() {
      try {
        const { data } = await fetchPublicSystemSettings();
        this.loginLogoUrl = data.login_logo_url || "";
      } catch (error) {
        this.loginLogoUrl = "";
      }
    },
    async loadCaptcha() {
      const { data } = await fetchCaptcha();
      this.captchaImage = this.normalizeCaptchaImage(data.image_base64);
      this.captchaId = data.captcha_id;
    },
    async loadResetCaptcha() {
      const { data } = await fetchCaptcha();
      this.resetCaptchaImage = this.normalizeCaptchaImage(data.image_base64);
      this.resetCaptchaId = data.captcha_id;
    },
    normalizeCaptchaImage(imageBase64) {
      const value = (imageBase64 || "").trim();
      if (!value) return "";
      return value.startsWith("data:image/") ? value : `data:image/png;base64,${value}`;
    },
    backToPassword() {
      this.step = "password";
      this.verifyToken = "";
      this.smsForm = { sms_code: "" };
      this.loadCaptcha();
    },
    openResetDialog() {
      this.showResetDialog = true;
      this.loadResetCaptcha();
    },
    resetResetForm() {
      this.resetForm = { phone: "", sms_code: "", name: "", id_card_no: "", new_password: "" };
      this.resetCaptchaCode = "";
      this.resetCaptchaId = "";
      this.resetCaptchaImage = "";
      this.resetSmsSending = false;
    },
    async submitPassword() {
      if (this.loading) return;
      this.$refs.passwordFormRef.validate(async (valid) => {
        if (!valid) return;
        this.loading = true;
        try {
          const { data } = await login({
            username: this.passwordForm.username,
            password: this.passwordForm.password,
            captcha_id: this.captchaId,
            captcha_code: this.passwordForm.captcha_code,
          });
          if (data.next_step === "sms_verify") {
            this.step = "sms";
            this.verifyToken = data.verify_token;
          } else {
            localStorage.setItem("ps_token", data.token);
            localStorage.setItem("ps_user", JSON.stringify(data.user));
            this.$router.push("/dashboard");
          }
        } catch (error) {
          this.$message.error(error.response?.data?.detail || "登录失败");
          this.loadCaptcha();
        } finally {
          this.loading = false;
        }
      });
    },
    async submitSms() {
      if (this.loading) return;
      this.$refs.smsFormRef.validate(async (valid) => {
        if (!valid) return;
        this.loading = true;
        try {
          const { data } = await loginAdminVerify({
            verify_token: this.verifyToken,
            sms_code: this.smsForm.sms_code,
          });
          localStorage.setItem("ps_token", data.token);
          localStorage.setItem("ps_user", JSON.stringify(data.user));
          this.$router.push("/dashboard");
        } catch (error) {
          this.$message.error(error.response?.data?.detail || "短信验证失败");
        } finally {
          this.loading = false;
        }
      });
    },
    async sendResetSmsCode() {
      if (!this.resetForm.phone) {
        this.$message.warning("请输入手机号");
        return;
      }
      if (!this.resetCaptchaCode) {
        this.$message.warning("请输入图片验证码");
        return;
      }
      this.resetSmsSending = true;
      try {
        await sendAuthSmsCode({
          phone: this.resetForm.phone,
          captcha_id: this.resetCaptchaId,
          captcha_code: this.resetCaptchaCode,
          scene: "password_reset",
        });
        this.$message.success("验证码已发送");
        this.resetCaptchaCode = "";
        this.loadResetCaptcha();
      } catch (error) {
        this.$message.error(error.response?.data?.detail || "发送失败");
        this.loadResetCaptcha();
      } finally {
        this.resetSmsSending = false;
      }
    },
    async submitResetPassword() {
      if (!this.resetForm.phone || !this.resetForm.sms_code || !this.resetForm.name || !this.resetForm.id_card_no || !this.resetForm.new_password) {
        this.$message.warning("请完整填写找回密码信息");
        return;
      }
      this.resetting = true;
      try {
        await resetPasswordBySms(this.resetForm);
        this.$message.success("密码已重置");
        this.showResetDialog = false;
        this.resetResetForm();
      } catch (error) {
        this.$message.error(error.response?.data?.detail || "重置失败");
      } finally {
        this.resetting = false;
      }
    },
  },
};
</script>

<style scoped>
.login-page {
  min-height: 100vh;
  display: grid;
  place-items: center;
  padding: 24px 24px 72px;
  background: linear-gradient(180deg, #f7fbff 0%, #ebf2ff 100%);
}
.login-card { width: 420px; padding: 32px; }
.login-header { margin-bottom: 24px; text-align: center; }
.login-logo {
  max-width: 220px;
  max-height: 88px;
  object-fit: contain;
  display: block;
  margin: 0 auto 14px;
}
.login-header p { margin: 0; color: #2f76ff; font-weight: 700; font-size: 30px; line-height: 1.4; }
.submit-btn { width: 100%; margin-top: 10px; }
.login-links { margin-top: 8px; text-align: right; }
.sms-actions { display: flex; flex-direction: column; gap: 10px; }
.sms-actions .submit-btn { margin: 0; }
.captcha-row { display: flex; gap: 12px; align-items: center; }
.captcha-image-wrap {
  width: 130px;
  height: 40px;
  flex: 0 0 130px;
  border-radius: 8px;
  cursor: pointer;
  border: 1px solid #dbe5f6;
  overflow: hidden;
  background: #f8fbff;
  display: flex;
  align-items: center;
  justify-content: center;
}
.captcha-image { width: 100%; height: 100%; object-fit: contain; display: block; }
.captcha-loading { color: #8b97aa; font-size: 12px; }
</style>
